import { useSnackbarStore } from "@/stores/snackbar"
import { AxiosError, isAxiosError } from "axios"
import errorReporter from "./errorReporter"
import { STATUS_MESSAGES, DEFAULT_ERROR_MESSAGE } from "./statusMessages"
import {
  BackofficeApiError,
  isBackofficeApiError,
} from "@/api/backofficeRest/backofficeApiError"
import type { ApiError } from "@/api/utils/types"

const getErrorMessage = (status: number): string =>
  STATUS_MESSAGES[status] || DEFAULT_ERROR_MESSAGE

const knownStatusMessage = (status?: number): string | undefined =>
  status ? STATUS_MESSAGES[status] : undefined

const isServerError = (status: number) => status >= 500

// A 4xx gateway message names what the agent can do; a 5xx one is internal
// detail, so the caller's own message reads better there.
const preferredMessage = (
  error: BackofficeApiError,
  callerMessage?: string,
): string | undefined =>
  isServerError(error.status)
    ? (callerMessage ?? error.backendMessage)
    : (error.backendMessage ?? callerMessage)

const setSnackbar = (message: string) => {
  const snackbar = useSnackbarStore()

  snackbar.addError(message)
}

const showAxiosError = (error: AxiosError, errorMessage?: string) => {
  const status = error.response?.status

  if (errorMessage) setSnackbar(errorMessage)
  else if (status) setSnackbar(getErrorMessage(status))
}

const errorHandler = {
  showApiError(error: ApiError, errorMessage?: string) {
    if (isBackofficeApiError(error)) {
      setSnackbar(preferredMessage(error, errorMessage) ?? error.userMessage)
      return
    }

    showAxiosError(error, errorMessage)
  },
  // The user-facing text for a caught rejection, for callers that render it
  // themselves (an in-page alert) rather than through the snackbar.
  userMessage(error: unknown, fallbackMessage: string): string {
    if (isBackofficeApiError(error)) {
      if (isServerError(error.status)) return fallbackMessage

      return (
        error.backendMessage ??
        knownStatusMessage(error.status) ??
        fallbackMessage
      )
    }

    const status = isAxiosError(error) ? error.response?.status : undefined

    return status ? getErrorMessage(status) : fallbackMessage
  },
  showCaughtError(error: unknown, fallbackMessage: string) {
    setSnackbar(errorHandler.userMessage(error, fallbackMessage))
  },
  reportApiError(error: ApiError) {
    if (isBackofficeApiError(error)) {
      errorReporter.report(error, error.reportContext)
      return
    }

    errorReporter.report(error)
  },
  handleApiError(error: ApiError, errorMessage?: string) {
    errorHandler.showApiError(error, errorMessage)
    errorHandler.reportApiError(error)
  },
  handleGenericError(error: Error) {
    setSnackbar(error.message)
    errorReporter.report(error)
  },
}

export default errorHandler
